import type { LearnTopic } from '../types';
import { $, width } from '../format';

export const expirationTopic: LearnTopic = {
  id: 'expiration',
  tier: 'position',
  title: 'What happens at expiration',
  summary: 'Inside both shorts, everything expires and you keep the credit. Through a short, that spread settles at its width.',
  body: [
    'If the stock closes between the two short strikes on expiration day, all four options expire worthless. You keep the whole credit, the held capital is released, and nothing else happens.',
    "If the stock closes through a short strike and past its wing, that spread is fully in the money. The short leg is assigned — assigned means you are obliged to buy or sell the shares at the strike — and the long leg is exercised — the long is used to buy or sell at its strike — and the two net out to the spread's width: you lose width minus credit on that side, the worst case and no more. The other side still expires worthless.",
    'If the stock closes between a short strike and its wing, the loss is only partial: the short is assigned but the long expires worthless, so the wing pays nothing and you are left holding shares (or short shares) over the weekend. That is the outcome to avoid — closing a breached side before the last hour avoids it.',
  ],
  explain(ctx) {
    const { symbol, shortPut, longPut, shortCall, longCall, credit, dte } = ctx;
    const putWidth = shortPut != null && longPut != null ? width(shortPut, longPut) : null;
    const callWidth = shortCall != null && longCall != null ? width(longCall, shortCall) : null;
    const widest = [putWidth, callWidth].filter((w): w is number => w != null);
    const maxWidth = widest.length ? Math.max(...widest) : null;

    // "0 days" and "-3 days" are both wrong readings of a real state: expiring today, and an
    // expiration Steward has not yet seen the close records for.
    const dteSentence = () => {
      if (dte == null) return 'The expiration date is not available.';
      if (dte === 0) return 'This position expires today.';
      if (dte < 0) return "This position's nearest expiration has passed; the legs close on the next sync.";
      return `This position has ${dte} days to its nearest expiration.`;
    };

    return [
      dteSentence(),
      shortPut != null && shortCall != null
        ? `If ${symbol} finishes between ${shortPut} and ${shortCall}, every leg expires worthless and you keep the credit.`
        : 'With one side open, the outcome depends on that side alone.',
      credit != null && maxWidth != null
        ? `Through a short strike, that side settles at its width minus the credit: at most ${$(maxWidth - credit)} per share.`
        : 'Through a short strike, that side settles at its width minus the credit.',
      "Expirations reach Steward on the next sync, as Schwab's expiration records, and the chain closes then.",
    ];
  },
  appliesTo() {
    return true;
  },
};
